import React, { useState, useEffect, useMemo } from 'react';
import { Row, Col } from 'reactstrap';
import { 
  PartyPopper, 
  Sparkles, 
  Calendar, 
  Heart, 
  Filter, 
  ChevronRight,
  Gift,
  Award
} from 'lucide-react';
import CelebrationCard from './CelebrationCard';
import CelebrationWorkspaceModal from './CelebrationWorkspaceModal';
import { fetchCelebrationsData, sortCelebrations } from './celebrationsData';
import { fireConfetti, fireCommunityShower } from './confettiUtil';
import './Celebrations.css';

const config = require('../../../config.json');

export default function CelebrationHub() {
  const [celebrations, setCelebrations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeFilter, setActiveFilter] = useState('all'); // 'all' | 'birthday' | 'ordination' | 'feast' | 'milestone'
  const [selectedCategory, setSelectedCategory] = useState('all'); // 'all' | 'student' | 'faculty' | 'staff'
  
  const hubRef = React.useRef(null);

  // Modal state
  const [workspaceCelebrant, setWorkspaceCelebrant] = useState(null);
  const [isWorkspaceOpen, setIsWorkspaceOpen] = useState(false);
  const [workspaceInitialTab, setWorkspaceInitialTab] = useState('ecard');

  useEffect(() => {
    loadCelebrations();
  }, []);

  // Automatic community confetti every 10 seconds - strictly confined inside the Celebration component
  useEffect(() => {
    // Initial celebration burst after brief mount delay
    const initialTimer = setTimeout(() => {
      if (typeof document !== 'undefined' && !document.hidden && hubRef.current) {
        fireCommunityShower(hubRef.current);
      }
    }, 1200);

    // Periodic celebration burst every 10 seconds
    const intervalId = setInterval(() => {
      if (typeof document !== 'undefined' && !document.hidden && hubRef.current) {
        fireCommunityShower(hubRef.current);
      }
    }, 10000);

    return () => {
      clearTimeout(initialTimer);
      clearInterval(intervalId);
    };
  }, []);

  const loadCelebrations = async () => {
    setLoading(true);
    try {
      const data = await fetchCelebrationsData(null, config);
      setCelebrations(data || []);
    } catch (err) {
      console.error("Error loading celebrations:", err);
    } finally {
      setLoading(false);
    }
  };

  // Filtered Celebrants based on occasion and category in ascending birthday/date order
  const filteredCelebrations = useMemo(() => {
    const list = celebrations.filter((item) => {
      const matchType = activeFilter === 'all' || item.occasionType === activeFilter;
      const matchCategory = selectedCategory === 'all' || item.category === selectedCategory;
      return matchType && matchCategory;
    });
    return sortCelebrations(list);
  }, [celebrations, activeFilter, selectedCategory]);

  const counts = useMemo(() => {
    return {
      all: celebrations.length,
      birthday: celebrations.filter(c => c.occasionType === 'birthday').length,
      ordination: celebrations.filter(c => c.occasionType === 'ordination').length,
      feast: celebrations.filter(c => c.occasionType === 'feast').length,
      milestone: celebrations.filter(c => c.occasionType === 'milestone').length,
      student: celebrations.filter(c => c.category === 'student').length,
      faculty: celebrations.filter(c => c.category === 'faculty').length,
      staff: celebrations.filter(c => c.category === 'staff').length
    };
  }, [celebrations]);

  const handleOpenWorkspace = (celebrant, tab = 'ecard') => {
    setWorkspaceCelebrant(celebrant);
    setWorkspaceInitialTab(tab);
    setIsWorkspaceOpen(true);
  };

  const handleCloseWorkspace = () => {
    setIsWorkspaceOpen(false);
  };

  const handleUpdateCelebrant = React.useCallback((celebrantId, updatedFields) => {
    if (!celebrantId || !updatedFields) return;

    setCelebrations((prevList) =>
      prevList.map((item) => {
        if (item.id === celebrantId || item.registrationNo === celebrantId || item.applicationNo === celebrantId) {
          const freshGreetings = updatedFields.greetings || item.greetings || item.initialGreetings || [];
          return {
            ...item,
            ...updatedFields,
            loveCount: updatedFields.loveCount !== undefined ? updatedFields.loveCount : item.loveCount,
            cakeCount: updatedFields.cakeCount !== undefined ? updatedFields.cakeCount : item.cakeCount,
            blessingCount: updatedFields.blessingCount !== undefined ? updatedFields.blessingCount : item.blessingCount,
            cheerCount: updatedFields.cheerCount !== undefined ? updatedFields.cheerCount : item.cheerCount,
            totalGreetings: updatedFields.totalGreetings !== undefined 
              ? updatedFields.totalGreetings 
              : (updatedFields.greetings ? updatedFields.greetings.length : item.totalGreetings),
            greetings: freshGreetings,
            initialGreetings: freshGreetings
          };
        }
        return item;
      })
    );
  }, []);

  return (
    <section 
      ref={hubRef}
      id="vj-celebrations-section-container"
      className="vj-celebrations-section position-relative"
    >
      {/* Section Header */}
      <div className="vj-cel-header">
        <div className="vj-cel-title-box">
          <div className="vj-cel-icon-badge">
            <PartyPopper size={28} />
            <span className="vj-cel-sparkle-dot" />
          </div>
          <div>
            <h3 className="vj-cel-heading">
              Community Celebrations & E-Card Hub 
              <span className="badge bg-danger rounded-pill fs-6 ms-2">
                {celebrations.length} Celebrations 🎉
              </span>
            </h3>
            <p className="vj-cel-subheading">
              Sharing the joy of birthdays, ordinations, feast days & milestones of our students, faculty, and staff.
            </p>
          </div>
        </div>

        {/* Occasion Filter Tabs */}
        <div className="vj-cel-filters">
          <button 
            className={`vj-filter-chip ${activeFilter === 'all' ? 'active' : ''}`}
            onClick={() => setActiveFilter('all')}
          >
            <span>🌟 All</span>
            <span className="vj-filter-count">{counts.all}</span>
          </button>
          
          <button 
            className={`vj-filter-chip ${activeFilter === 'birthday' ? 'active' : ''}`}
            onClick={() => setActiveFilter('birthday')}
          >
            <span>🎂 Birthdays</span>
            <span className="vj-filter-count">{counts.birthday}</span>
          </button>

          <button 
            className={`vj-filter-chip ${activeFilter === 'ordination' ? 'active' : ''}`}
            onClick={() => setActiveFilter('ordination')}
          >
            <span>🕊️ Ordinations</span>
            <span className="vj-filter-count">{counts.ordination}</span>
          </button>

          <button 
            className={`vj-filter-chip ${activeFilter === 'feast' ? 'active' : ''}`}
            onClick={() => setActiveFilter('feast')}
          >
            <span>👑 Feast Days</span>
            <span className="vj-filter-count">{counts.feast}</span>
          </button>

          <button 
            className={`vj-filter-chip ${activeFilter === 'milestone' ? 'active' : ''}`}
            onClick={() => setActiveFilter('milestone')}
          >
            <span>🏆 Milestones</span>
            <span className="vj-filter-count">{counts.milestone}</span>
          </button>
        </div>
      </div>

      {/* Role / Category Sub-filter */}
      <div className="d-flex align-items-center justify-content-between mb-4 flex-wrap gap-2">
        <div className="d-flex align-items-center gap-2">
          <span className="text-muted fw-bold" style={{ fontSize: '0.82rem' }}>
            <Filter size={14} className="me-1" /> Category:
          </span>
          <div className="btn-group btn-group-sm" role="group">
            <button 
              type="button" 
              className={`btn btn-sm ${selectedCategory === 'all' ? 'btn-dark' : 'btn-outline-secondary'}`}
              onClick={() => setSelectedCategory('all')}
            >
              All Roles ({counts.all})
            </button>
            <button 
              type="button" 
              className={`btn btn-sm ${selectedCategory === 'student' ? 'btn-dark' : 'btn-outline-secondary'}`}
              onClick={() => setSelectedCategory('student')}
            >
              Students ({counts.student})
            </button>
            <button 
              type="button" 
              className={`btn btn-sm ${selectedCategory === 'faculty' ? 'btn-dark' : 'btn-outline-secondary'}`}
              onClick={() => setSelectedCategory('faculty')}
            >
              Faculty ({counts.faculty})
            </button>
            <button 
              type="button" 
              className={`btn btn-sm ${selectedCategory === 'staff' ? 'btn-dark' : 'btn-outline-secondary'}`}
              onClick={() => setSelectedCategory('staff')}
            >
              Staff ({counts.staff})
            </button>
          </div>
        </div>
{/*}
        <button 
          className="btn btn-sm btn-outline-danger rounded-pill px-3 d-inline-flex align-items-center gap-1 fw-bold shadow-sm"
          onClick={() => fireCommunityShower(hubRef.current)}
          title="Click to shower community confetti now! (Auto-celebrates every 10s)"
        >
          <Sparkles size={14} className="text-warning" /> Shower Community Confetti! 🎊
        </button> */}
      </div>

      {/* Celebrations Grid */}
      {filteredCelebrations.length > 0 ? (
        <Row className="g-4">
          {filteredCelebrations.map((cel) => (
            <Col xl={3} lg={4} md={6} sm={12} key={cel.id} className="d-flex">
              <CelebrationCard 
                celebrant={cel} 
                onOpenWorkspace={handleOpenWorkspace}
                onUpdateCelebrant={handleUpdateCelebrant}
              />
            </Col>
          ))}
        </Row>
      ) : (
        <div className="vj-no-celebrations-box">
          <div className="vj-icon-badge mx-auto mb-2" style={{ background: '#fef3c7', color: '#b45309' }}>
            <Gift size={24} />
          </div>
          <h5 className="fw-bold text-dark-main">No Celebrations in this Category Today</h5>
          <p className="text-muted mb-3" style={{ fontSize: '0.9rem' }}>
            Check other tabs or celebrate with our overall community.
          </p>
          <button 
            className="btn btn-sm btn-dark rounded-pill px-3"
            onClick={() => { setActiveFilter('all'); setSelectedCategory('all'); }}
          >
            View All Celebrations
          </button>
        </div>
      )}

      {/* Interactive Celebration Workspace Modal */}
      <CelebrationWorkspaceModal 
        celebrant={workspaceCelebrant}
        isOpen={isWorkspaceOpen}
        onClose={handleCloseWorkspace}
        initialTab={workspaceInitialTab}
        onUpdateCelebrant={handleUpdateCelebrant}
      />
    </section>
  );
}
